/**
 * Statistics utility functions
 */

import { FEATURE_LABELS } from "./constants";

/**
 * Format milliseconds to human readable string
 * @param {number} ms - Milliseconds
 * @returns {string} Formatted string (e.g., "150ms", "2.35s", "1.5min")
 */
export const formatMs = (ms) => {
  if (ms === null || ms === undefined) return "-";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 60000).toFixed(1)}min`;
};

/**
 * Format timestamp to time string
 * @param {string} ts - ISO timestamp
 * @returns {string} Formatted time (e.g., "14:35:22")
 */
export const formatTime = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

/**
 * Get user-friendly feature label
 * @param {string} feature - Feature key (e.g., "face_size")
 * @returns {string} Human-readable label
 */
export const getFeatureLabel = (feature) => {
  const info = FEATURE_LABELS[feature];
  if (info) return info.label;
  // Fallback: convert snake_case to Title Case
  return feature.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Get feature description
 * @param {string} feature - Feature key
 * @returns {string} Description or empty string
 */
export const getFeatureDesc = (feature) => {
  const info = FEATURE_LABELS[feature];
  return info?.desc || "";
};

/**
 * Calculate derived stats from API response
 * @param {object} stats - Stats from API
 * @returns {object} Derived statistics
 */
export const calculateDerivedStats = (stats) => {
  if (!stats) return {};
  const byStatus = stats.by_status || {};
  return {
    total: stats.total || 0,
    done: byStatus.done || 0,
    // Pipeline processing (detecting/identifying deprecated, kept for historical data)
    processing: (byStatus.processing || 0) + (byStatus.detecting || 0) + (byStatus.identifying || 0),
    // V2: to_tag is stats.to_tag (pending with unlabeled faces)
    toTag: stats.to_tag || 0,
    pending: stats.pending || byStatus.pending || 0,
    exported: byStatus.exported || 0,
    attention: stats.attention || byStatus.attention || 0,
    errors: byStatus.error || 0,
  };
};

/**
 * Calculate total pipeline time from metrics
 * @param {object} metricsStats - Metrics stats from API
 * @returns {number|null} Total pipeline time in ms
 */
export const calculateTotalPipelineMs = (metricsStats) => {
  if (!metricsStats?.avg_ms) return null;
  const ms = metricsStats.avg_ms;
  return (ms.merovingian || 0) + (ms.sati || 0) + (ms.sentinel || 0) + (ms.seraph || 0);
};

/**
 * Get today's date in ISO format
 * @returns {string} Date string (YYYY-MM-DD)
 */
export const getTodayISO = () => new Date().toISOString().split("T")[0];

/**
 * Get date N days ago in ISO format
 * @param {number} days - Number of days ago
 * @returns {string} Date string (YYYY-MM-DD)
 */
export const getDaysAgoISO = (days) =>
  new Date(Date.now() - days * 86400000).toISOString().split("T")[0];
